import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useContextMenu } from '../shared/ContextMenu';
import { useContextMenuSettings } from '../../contexts/ContextMenuContext';
import TaskCard from './TaskCard';
import TaskModal from './TaskModal';
import BoardMembers from './BoardMembers';
import SubmissionReview from './SubmissionReview';
import ExportModal from './ExportModal';
import ArchiveLibrary from './ArchiveLibrary';
import ArchiveModal from './ArchiveModal';
import Navbar from '../shared/Navbar';
import StorageBanner from '../shared/StorageBanner';
import '../../css/BoardView.css';

const BoardView = () => {
    const { boardId, taskNumber } = useParams();
    const { apiCall, user } = useAuth();
    const navigate = useNavigate();
    const autoOpenedRef = useRef(null);
    const [board, setBoard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [accessError, setAccessError] = useState(null); // { publicBoard: object|null }
    const [addingTaskColumn, setAddingTaskColumn] = useState(null);
    const [newTaskTitle, setNewTaskTitle] = useState('');
    const [selectedTask, setSelectedTask] = useState(null);
    const [addingColumn, setAddingColumn] = useState(false);
    const [newColumnName, setNewColumnName] = useState('');
    const [draggedTask, setDraggedTask] = useState(null);
    const [editingColumn, setEditingColumn] = useState(null);
    const [editColumnName, setEditColumnName] = useState('');
    const [editColumnColor, setEditColumnColor] = useState('');
    const [editingBoard, setEditingBoard] = useState(false);
    const [editBoardName, setEditBoardName] = useState('');
    const [editBoardAbbr, setEditBoardAbbr] = useState('');
    const [editBoardDesc, setEditBoardDesc] = useState('');
    const [storageFull, setStorageFull] = useState(false);
    const [addingTask, setAddingTask] = useState(false);
    const [addingColumnLoading, setAddingColumnLoading] = useState(false);
    const [deletingColumnId, setDeletingColumnId] = useState(null);
    const [savingColumn, setSavingColumn] = useState(false);
    const [savingBoard, setSavingBoard] = useState(false);
    const [movingColumnDir, setMovingColumnDir] = useState(null);
    const [movingTask, setMovingTask] = useState(false);
    const [showMembers, setShowMembers] = useState(false);
    const [editBoardPublic, setEditBoardPublic] = useState(false);
    const [editPublicTaskFilter, setEditPublicTaskFilter] = useState('all');
    const [editPublicLabelFilter, setEditPublicLabelFilter] = useState([]);
    const [editPublicShowMembers, setEditPublicShowMembers] = useState(false);
    const [editPublicShowContact, setEditPublicShowContact] = useState(false);
    const [publicLabelInput, setPublicLabelInput] = useState('');
    const [publicLabelDropdown, setPublicLabelDropdown] = useState(false);
    const [publicLabelHighlight, setPublicLabelHighlight] = useState(-1);
    const [editPublicAllowComments, setEditPublicAllowComments] = useState(false);
    const [editPublicHiddenColumns, setEditPublicHiddenColumns] = useState([]);
    const [editPublicShowArchive, setEditPublicShowArchive] = useState(false);
    const [editPublicAllowTaskSubmissions, setEditPublicAllowTaskSubmissions] = useState(false);
    const [editTaskReviewRoles, setEditTaskReviewRoles] = useState([]);
    const [showSubmissionReview, setShowSubmissionReview] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const [showExport, setShowExport] = useState(false);
    const [showArchiveLibrary, setShowArchiveLibrary] = useState(false);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [tasksForArchive, setTasksForArchive] = useState([]);
    const [archivingByLabel, setArchivingByLabel] = useState(null);
    const [activeLabelFilter, setActiveLabelFilter] = useState(null);
    const [deletingLabel, setDeletingLabel] = useState(null);
    const [showLabelDropdown, setShowLabelDropdown] = useState(false);
    const [labelDropdownSearch, setLabelDropdownSearch] = useState('');

    const [childTaskParent, setChildTaskParent] = useState(null);
    const [childTaskTitle, setChildTaskTitle] = useState('');
    const [creatingChildTask, setCreatingChildTask] = useState(false);

    const userRole = board?.user_role || null;
    const canEdit = userRole === 'OWNER' || userRole === 'ADMIN' || userRole === 'EDITOR';
    const canAdmin = userRole === 'OWNER' || userRole === 'ADMIN';
    const canReview = userRole === 'OWNER' || (board?.task_review_roles || []).includes(userRole);
    const pendingSubmissions = board?.pending_submissions_count || 0;

    const { enhancedRightClickEnabled } = useContextMenuSettings();

    // ─── Enhanced Right-Click ────────────────────────────────────────
    const globalBoardMenuOptions = [
        {
            label: "Back to Boards",
            icon: <i className="fas fa-arrow-left"></i>,
            onClick: () => navigate("/"),
        },
        ...(canAdmin ? [{
            label: "Edit Board",
            icon: <i className="fas fa-pen"></i>,
            onClick: () => handleOpenEditBoard(),
        }] : []),
        {
            label: "Members",
            icon: <i className="fas fa-users"></i>,
            onClick: () => setShowMembers(true),
        },
        ...(canReview ? [{
            label: pendingSubmissions > 0 ? `Review Submissions (${pendingSubmissions})` : "Review Submissions",
            icon: <i className="fas fa-inbox"></i>,
            onClick: () => setShowSubmissionReview(true),
        }] : []),
        ...(canAdmin ? [{
            label: "Add Column",
            icon: <i className="fas fa-plus-circle"></i>,
            onClick: () => setAddingColumn(true),
        }] : []),
        {
            label: "Export for AI",
            icon: <i className="fas fa-file-export"></i>,
            onClick: () => setShowExport(true),
        },
        {
            label: "Archive Library",
            icon: <i className="fas fa-archive"></i>,
            onClick: () => setShowArchiveLibrary(true),
        },
        { separator: true },
        {
            label: "Bluebird Documentation",
            icon: <i className="fas fa-book"></i>,
            url: "https://bluebird-documentation.com/",
            onClick: () => window.open("https://bluebird-documentation.com/", "_blank", "noopener,noreferrer"),
        },
    ];

    const { handleContextMenu, ContextMenuComponent } = useContextMenu(globalBoardMenuOptions);

    const { showContextMenu: showTaskContextMenu, ContextMenuComponent: TaskContextMenuComponent } = useContextMenu((taskData) => {
        if (!taskData) return [];
        const taskUrl = `${window.location.origin}/board/${boardId}/task/${taskData.board_number}`;
        return [
            {
                label: 'Open Task',
                icon: <i className="fas fa-expand-alt" />,
                onClick: () => openTask(taskData),
            },
            {
                label: 'Open in New Tab',
                icon: <i className="fas fa-external-link-alt" />,
                url: taskUrl,
                onClick: () => window.open(taskUrl, '_blank', 'noopener,noreferrer'),
            },
            {
                label: 'Copy Link',
                icon: <i className="fas fa-link" />,
                onClick: () => navigator.clipboard.writeText(taskUrl),
            },
            ...(canEdit ? [
                { separator: true },
                {
                    label: 'Create Child Task',
                    icon: <i className="fas fa-sitemap" />,
                    onClick: () => {
                        setChildTaskParent(taskData);
                        setChildTaskTitle('');
                    },
                },
                { separator: true },
                {
                    label: 'Archive Task',
                    icon: <i className="fas fa-archive" />,
                    onClick: () => handleArchiveSingleTask(taskData.id),
                },
                {
                    label: 'Delete Task',
                    icon: <i className="fas fa-trash-alt" />,
                    onClick: () => {
                        if (window.confirm(`Delete "${taskData.title}"? This cannot be undone.`)) {
                            handleDeleteTask(taskData.id);
                        }
                    },
                },
            ] : []),
        ];
    });

    const handleTaskContextMenu = (e, task) => {
        if (!enhancedRightClickEnabled) return;
        e.preventDefault();
        e.stopPropagation();
        showTaskContextMenu(e.clientX, e.clientY, task);
    };

    const COLUMN_COLORS = [
        '#5B92E5', '#4A7BC8', '#F5A623', '#7ED321',
        '#E55B5B', '#B45BE5', '#5BE5C3', '#E5C35B',
        '#FF6B6B', '#48DBFB', '#FF9FF3', '#54A0FF',
    ];

    const fetchBoard = useCallback(async () => {
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/`);
            if (response.ok) {
                const data = await response.json();
                setBoard(data);
            } else {
                // No access (403) or not found (404) — check if board is publicly viewable
                let publicBoard = null;
                try {
                    const pubRes = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/tasks/boards/${boardId}/public/`);
                    if (pubRes.ok) publicBoard = await pubRes.json();
                } catch {
                    // ignore — public check is best-effort
                }
                setAccessError({ publicBoard });
            }
        } catch (err) {
            console.error('Error fetching board:', err);
        }
        setLoading(false);
    }, [apiCall, boardId]);

    useEffect(() => {
        fetchBoard();
    }, [fetchBoard]);

    // ─── Task URL routing (BBTM-29) ─────────────────────────────────
    const openTask = useCallback((task) => {
        setSelectedTask(task);
        navigate(`/board/${boardId}/task/${task.board_number}`, { replace: true });
    }, [boardId, navigate]);

    const closeTask = useCallback(() => {
        setSelectedTask(null);
        navigate(`/board/${boardId}`, { replace: true });
    }, [boardId, navigate]);

    // Auto-open task when URL contains a task number
    useEffect(() => {
        if (!board || !taskNumber) return;
        if (autoOpenedRef.current === taskNumber) return;
        const boardNum = parseInt(taskNumber, 10);
        const task = board.columns.flatMap(c => c.tasks).find(t => t.board_number === boardNum);
        if (task) {
            autoOpenedRef.current = taskNumber;
            setSelectedTask(task);
        }
    }, [board, taskNumber]);

    const handleAddTask = async (columnId) => {
        if (!newTaskTitle.trim()) return;

        setAddingTask(true);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/tasks/create/`, {
                method: 'POST',
                body: JSON.stringify({
                    column_id: columnId,
                    title: newTaskTitle.trim(),
                }),
            });

            if (response.ok) {
                await fetchBoard();
                setNewTaskTitle('');
                setAddingTaskColumn(null);
                setStorageFull(false);
            } else if (response.status === 403) {
                const data = await response.json();
                if (data.storage_full) {
                    setStorageFull(true);
                    setAddingTaskColumn(null);
                    setNewTaskTitle('');
                }
            }
        } catch (err) {
            console.error('Error creating task:', err);
        }
        setAddingTask(false);
    };

    const handleAddColumn = async () => {
        if (!newColumnName.trim()) return;

        setAddingColumnLoading(true);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/columns/create/`, {
                method: 'POST',
                body: JSON.stringify({ name: newColumnName.trim() }),
            });

            if (response.ok) {
                await fetchBoard();
                setNewColumnName('');
                setAddingColumn(false);
            }
        } catch (err) {
            console.error('Error creating column:', err);
        }
        setAddingColumnLoading(false);
    };

    const handleDeleteColumn = async (columnId) => {
        if (!window.confirm('Delete this column and all its tasks?')) return;

        setDeletingColumnId(columnId);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/columns/${columnId}/delete/`, {
                method: 'DELETE',
            });
            if (response.ok) {
                await fetchBoard();
            }
        } catch (err) {
            console.error('Error deleting column:', err);
        }
        setDeletingColumnId(null);
    };

    const handleOpenEditColumn = (column) => {
        setEditingColumn(column);
        setEditColumnName(column.name);
        setEditColumnColor(column.color || '#5B92E5');
    };

    const handleSaveColumnEdit = async () => {
        if (!editingColumn || !editColumnName.trim()) return;

        setSavingColumn(true);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/columns/${editingColumn.id}/update/`, {
                method: 'PUT',
                body: JSON.stringify({
                    name: editColumnName.trim(),
                    color: editColumnColor,
                }),
            });
            if (response.ok) {
                await fetchBoard();
                setEditingColumn(null);
            }
        } catch (err) {
            console.error('Error updating column:', err);
        }
        setSavingColumn(false);
    };

    const handleOpenEditBoard = () => {
        setEditBoardName(board.name);
        setEditBoardAbbr(board.abbreviation || '');
        setEditBoardDesc(board.description || '');
        setEditBoardPublic(board.is_public || false);
        setEditPublicTaskFilter(board.public_task_filter || 'all');
        setEditPublicLabelFilter(board.public_label_filter || []);
        setEditPublicShowMembers(board.public_show_members || false);
        setEditPublicShowContact(board.public_show_contact || false);
        setEditPublicAllowComments(board.public_allow_comments || false);
        setEditPublicHiddenColumns(board.public_hidden_columns || []);
        setEditPublicShowArchive(board.public_show_archive || false);
        setEditPublicAllowTaskSubmissions(board.public_allow_task_submissions || false);
        setEditTaskReviewRoles(board.task_review_roles || []);
        setPublicLabelInput('');
        setPublicLabelDropdown(false);
        setPublicLabelHighlight(-1);
        setEditingBoard(true);
    };

    const handleSaveBoardEdit = async () => {
        if (!editBoardName.trim()) return;

        setSavingBoard(true);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/update/`, {
                method: 'PUT',
                body: JSON.stringify({
                    name: editBoardName.trim(),
                    abbreviation: editBoardAbbr.trim().toUpperCase(),
                    description: editBoardDesc.trim(),
                    is_public: editBoardPublic,
                    public_task_filter: editPublicTaskFilter,
                    public_label_filter: editPublicLabelFilter,
                    public_show_members: editPublicShowMembers,
                    public_show_contact: editPublicShowContact,
                    public_allow_comments: editPublicAllowComments,
                    public_hidden_columns: editPublicHiddenColumns,
                    public_show_archive: editPublicShowArchive,
                    public_allow_task_submissions: editPublicAllowTaskSubmissions,
                    ...(userRole === 'OWNER' ? { task_review_roles: editTaskReviewRoles } : {}),
                }),
            });
            if (response.ok) {
                await fetchBoard();
                setEditingBoard(false);
            }
        } catch (err) {
            console.error('Error updating board:', err);
        }
        setSavingBoard(false);
    };

    const handleMoveColumn = async (columnId, direction) => {
        if (!board) return;
        const cols = [...board.columns];
        const idx = cols.findIndex(c => c.id === columnId);
        if (idx < 0) return;
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= cols.length) return;

        setMovingColumnDir(direction);
        // Swap positions
        [cols[idx], cols[newIdx]] = [cols[newIdx], cols[idx]];
        const column_order = cols.map(c => c.id);

        try {
            const response = await apiCall(`/tasks/boards/${boardId}/columns/reorder/`, {
                method: 'PUT',
                body: JSON.stringify({ column_order }),
            });
            if (response.ok) {
                await fetchBoard();
                setEditingColumn(null);
            }
        } catch (err) {
            console.error('Error reordering columns:', err);
        }
        setMovingColumnDir(null);
    };

    const handleDeleteTask = async (taskId) => {
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/tasks/${taskId}/delete/`, {
                method: 'DELETE',
            });
            if (response.ok) {
                await fetchBoard();
                closeTask();
            }
        } catch (err) {
            console.error('Error deleting task:', err);
        }
    };

    const handleDeleteTasksByLabel = async (label) => {
        const tasksToDelete = board.columns.flatMap(col =>
            col.tasks.filter(t => !t.is_archived && t.labels?.includes(label))
        );
        if (tasksToDelete.length === 0) return;
        const noun = tasksToDelete.length === 1 ? 'task' : 'tasks';
        if (!window.confirm(`Delete ${tasksToDelete.length} ${noun} with label "${label}"? This cannot be undone.`)) return;

        setDeletingLabel(label);
        try {
            for (const task of tasksToDelete) {
                await apiCall(`/tasks/boards/${boardId}/tasks/${task.id}/delete/`, { method: 'DELETE' });
            }
            if (activeLabelFilter === label) setActiveLabelFilter(null);
            await fetchBoard();
        } catch (err) {
            console.error('Error deleting tasks by label:', err);
        }
        setDeletingLabel(null);
    };

    const handleArchiveTasks = async (taskIds, mode, labelOrGroupName) => {
        try {
            const body = { task_ids: taskIds, mode };
            if (mode === 'label') body.label = labelOrGroupName;
            if (mode === 'custom') body.group_name = labelOrGroupName;
            const response = await apiCall(`/tasks/boards/${boardId}/archive/`, {
                method: 'POST',
                body: JSON.stringify(body),
            });
            if (response.ok) {
                await fetchBoard();
                closeTask();
                setShowArchiveModal(false);
                setTasksForArchive([]);
            }
        } catch (err) {
            console.error('Error archiving tasks:', err);
        }
    };

    const handleArchiveByLabel = async (label) => {
        setArchivingByLabel(label);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/archive/by-label/`, {
                method: 'POST',
                body: JSON.stringify({ label }),
            });
            if (response.ok) {
                if (activeLabelFilter === label) setActiveLabelFilter(null);
                await fetchBoard();
            }
        } catch (err) {
            console.error('Error archiving by label:', err);
        }
        setArchivingByLabel(null);
    };

    const handleArchiveSingleTask = (taskId) => {
        const task = board.columns.flatMap(col => col.tasks).find(t => t.id === taskId);
        if (task) {
            setTasksForArchive([task]);
            setShowArchiveModal(true);
        }
    };

    const handleCreateChildTask = async () => {
        if (!childTaskTitle.trim() || !childTaskParent || creatingChildTask) return;
        setCreatingChildTask(true);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/tasks/create/`, {
                method: 'POST',
                body: JSON.stringify({
                    column_id: childTaskParent.column,
                    title: childTaskTitle.trim(),
                }),
            });
            if (response.ok) {
                const newTask = await response.json();
                await apiCall(`/tasks/boards/${boardId}/tasks/${newTask.id}/parent/`, {
                    method: 'POST',
                    body: JSON.stringify({ parent_task_id: childTaskParent.id }),
                });
                await fetchBoard();
                setChildTaskParent(null);
                setChildTaskTitle('');
                setStorageFull(false);
            } else if (response.status === 403) {
                const data = await response.json();
                if (data.storage_full) {
                    setStorageFull(true);
                    setChildTaskParent(null);
                    setChildTaskTitle('');
                }
            }
        } catch (err) {
            console.error('Error creating child task:', err);
        }
        setCreatingChildTask(false);
    };

    const handleUpdateTask = async (taskId, updates) => {
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/tasks/${taskId}/update/`, {
                method: 'PUT',
                body: JSON.stringify(updates),
            });
            if (response.ok) {
                await fetchBoard();
                const updatedTask = await response.json();
                setSelectedTask(updatedTask);
            }
        } catch (err) {
            console.error('Error updating task:', err);
        }
    };

    const handleImmediateUpdate = (fields) => {
        setSelectedTask(prev => prev ? { ...prev, ...fields } : prev);
        if (!selectedTask) return;
        const taskId = selectedTask.id;
        setBoard(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                columns: prev.columns.map(col => ({
                    ...col,
                    tasks: col.tasks.map(t => t.id === taskId ? { ...t, ...fields } : t),
                })),
            };
        });
    };

    const handleMoveTask = async (taskId, newColumnId, newPosition) => {
        setMovingTask(true);
        try {
            const response = await apiCall(`/tasks/boards/${boardId}/tasks/${taskId}/move/`, {
                method: 'PUT',
                body: JSON.stringify({
                    column_id: newColumnId,
                    position: newPosition,
                }),
            });
            if (response.ok) {
                await fetchBoard();
            }
        } catch (err) {
            console.error('Error moving task:', err);
        }
        setMovingTask(false);
    };

    // ─── Drag & Drop ────────────────────────────────────────────────────
    const handleDragStart = (e, task) => {
        setDraggedTask(task);
        e.dataTransfer.effectAllowed = 'move';
        // Add a slight delay to allow the drag image to render
        setTimeout(() => {
            e.target.classList.add('dragging');
        }, 0);
    };

    const handleDragEnd = (e) => {
        e.target.classList.remove('dragging');
        setDraggedTask(null);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, columnId) => {
        e.preventDefault();
        if (draggedTask && draggedTask.column !== columnId) {
            handleMoveTask(draggedTask.id, columnId, 0);
        }
        setDraggedTask(null);
    };

    if (loading) {
        return (
            <div className="BoardView">
                <Navbar />
                <div className="LoadingScreen"><div className="LoadingScreen__spinner" /></div>
            </div>
        );
    }

    if (accessError) {
        const pub = accessError.publicBoard;
        return (
            <div className="BoardView">
                <Navbar />
                <div className="BoardView__access-error">
                    <div className="BoardView__access-error-card">
                        <i className="fas fa-lock BoardView__access-error-icon" />
                        <h2 className="BoardView__access-error-title">Access Denied</h2>
                        <p className="BoardView__access-error-body">
                            You don't have permission to view this board.
                        </p>
                        {pub && (
                            <div className="BoardView__access-error-public">
                                <div className="BoardView__access-error-board-info">
                                    {pub.abbreviation && (
                                        <span className="BoardView__access-error-abbr">{pub.abbreviation}</span>
                                    )}
                                    <span className="BoardView__access-error-board-name">{pub.name}</span>
                                </div>
                                <p className="BoardView__access-error-hint">
                                    This board has a public view available.
                                </p>
                            </div>
                        )}
                        <div className="BoardView__access-error-actions">
                            {pub && (
                                <button
                                    className="BoardView__access-error-btn BoardView__access-error-btn--primary"
                                    onClick={() => navigate(`/public/board/${boardId}`)}
                                >
                                    <i className="fas fa-globe" /> View Public Board
                                </button>
                            )}
                            <button
                                className="BoardView__access-error-btn BoardView__access-error-btn--secondary"
                                onClick={() => navigate('/')}
                            >
                                <i className="fas fa-home" /> Go to My Boards
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!board) {
        return (
            <div className="BoardView">
                <Navbar />
                <div className="BoardView__empty">Board not found</div>
            </div>
        );
    }

    const allBoardLabels = [...new Set(board.columns.flatMap(col => col.tasks.flatMap(t => t.labels || [])))];
    const filteredDropdownLabels = allBoardLabels.filter(l => l.toLowerCase().includes(labelDropdownSearch.toLowerCase()));

    return (
        <div className="BoardView" onContextMenu={handleContextMenu}>
            <Navbar />
            {storageFull && (
                <div className="BoardView__storage-banner-wrapper">
                    <StorageBanner onDismiss={() => setStorageFull(false)} />
                </div>
            )}
            <div className="BoardView__header">
                <button className="BoardView__back" onClick={() => navigate('/')}>← Back</button>
                <div className="BoardView__info">
                    <div className="BoardView__title-row">
                        {board.abbreviation && (
                            <span className="BoardView__abbr">{board.abbreviation}</span>
                        )}
                        <h1 className="BoardView__title">{board.name}</h1>
                        {movingTask && <span className="BoardView__task-moving" />}
                        <button
                            className="BoardView__members-btn"
                            onClick={() => setShowMembers(true)}
                            title="Board members"
                        >
                            <i className="fas fa-users" />
                            {board.members && board.members.length > 0 && (
                                <span className="BoardView__members-count">{board.members.length + 1}</span>
                            )}
                        </button>
                        {canReview && (
                            <button
                                className="BoardView__review-btn"
                                onClick={() => setShowSubmissionReview(true)}
                                title="Review task submissions"
                            >
                                <i className="fas fa-inbox" /> Review
                                {pendingSubmissions > 0 && (
                                    <span className="BoardView__review-badge">{pendingSubmissions}</span>
                                )}
                            </button>
                        )}
                        {canAdmin && (
                            <button
                                className="BoardView__edit-board-btn"
                                onClick={handleOpenEditBoard}
                                title="Edit board"
                            >
                                ✎
                            </button>
                        )}
                        {board.is_public && (
                            <button
                                className="BoardView__share-btn"
                                onClick={() => {
                                    navigator.clipboard.writeText(`${window.location.origin}/public/board/${boardId}`);
                                    setCopiedLink(true);
                                    setTimeout(() => setCopiedLink(false), 2000);
                                }}
                                title="Copy public link"
                            >
                                <i className={copiedLink ? 'fas fa-check' : 'fas fa-share-alt'} />
                                {copiedLink ? 'Copied!' : 'Share'}
                            </button>
                        )}
                        <button
                            className="BoardView__export-btn"
                            onClick={() => setShowExport(true)}
                            title="Export tasks for AI"
                        >
                            <i className="fas fa-file-export" /> Export
                        </button>
                        <button
                            className="BoardView__export-btn"
                            onClick={() => setShowArchiveLibrary(true)}
                            title="Archive library"
                        >
                            <i className="fas fa-archive" /> Archive
                        </button>
                        {board.linked_workspace && (
                            <span className="BoardView__linked-workspace-badge" title={`Linked to workspace: ${board.linked_workspace.workspace_name}`}>
                                <i className="fas fa-link" /> {board.linked_workspace.workspace_name}
                            </span>
                        )}
                        {allBoardLabels.length > 0 && (
                            <div className="BoardView__label-filter-wrapper">
                                <button
                                    className={`BoardView__label-filter-btn ${activeLabelFilter ? 'BoardView__label-filter-btn--active' : ''}`}
                                    onClick={() => { setShowLabelDropdown(v => !v); setLabelDropdownSearch(''); }}
                                    title="Filter by label"
                                >
                                    <i className="fas fa-tag" />
                                    <span>{activeLabelFilter || 'Labels'}</span>
                                    <i className={`fas fa-chevron-${showLabelDropdown ? 'up' : 'down'} BoardView__label-filter-chevron`} />
                                </button>
                                {showLabelDropdown && (
                                    <>
                                        <div className="BoardView__label-dropdown-backdrop" onClick={() => setShowLabelDropdown(false)} />
                                        <div className="BoardView__label-dropdown">
                                            <div className="BoardView__label-dropdown-header">
                                                <span>Filter by label</span>
                                                {activeLabelFilter && (
                                                    <button
                                                        className="BoardView__label-dropdown-clear"
                                                        onClick={() => { setActiveLabelFilter(null); setShowLabelDropdown(false); }}
                                                    >
                                                        Clear
                                                    </button>
                                                )}
                                            </div>
                                            {allBoardLabels.length > 5 && (
                                                <div className="BoardView__label-dropdown-search">
                                                    <i className="fas fa-search" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search labels..."
                                                        value={labelDropdownSearch}
                                                        onChange={e => setLabelDropdownSearch(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                            )}
                                            <div className="BoardView__label-dropdown-list">
                                                {filteredDropdownLabels.length === 0 && (
                                                    <div className="BoardView__label-dropdown-empty">No labels match</div>
                                                )}
                                                {filteredDropdownLabels.map(label => (
                                                    <div
                                                        key={label}
                                                        className={`BoardView__label-dropdown-item ${activeLabelFilter === label ? 'BoardView__label-dropdown-item--active' : ''}`}
                                                    >
                                                        <button
                                                            className="BoardView__label-dropdown-item-name"
                                                            onClick={() => { setActiveLabelFilter(activeLabelFilter === label ? null : label); setShowLabelDropdown(false); setLabelDropdownSearch(''); }}
                                                        >
                                                            <span className="BoardView__label-dropdown-item-check">
                                                                {activeLabelFilter === label && <i className="fas fa-check" />}
                                                            </span>
                                                            {label}
                                                        </button>
                                                        {canEdit && (
                                                            <button
                                                                className="BoardView__label-dropdown-item-archive"
                                                                onClick={() => { handleArchiveByLabel(label); setShowLabelDropdown(false); }}
                                                                disabled={archivingByLabel === label}
                                                                title={`Archive all tasks with label "${label}"`}
                                                            >
                                                                {archivingByLabel === label
                                                                    ? <span className="BoardView__label-chip-spinner" />
                                                                    : <i className="fas fa-archive" />}
                                                            </button>
                                                        )}
                                                        {canEdit && (
                                                            <button
                                                                className="BoardView__label-dropdown-item-delete"
                                                                onClick={() => handleDeleteTasksByLabel(label)}
                                                                disabled={deletingLabel === label}
                                                                title={`Delete all tasks with label "${label}"`}
                                                            >
                                                                {deletingLabel === label
                                                                    ? <span className="BoardView__label-chip-spinner" />
                                                                    : <i className="fas fa-trash-alt" />}
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    {board.description && (
                        <p className="BoardView__description">{board.description}</p>
                    )}
                </div>
            </div>

            <div className="BoardView__columns">
                {board.columns.map(column => (
                    <div
                        key={column.id}
                        className={`BoardView__column ${draggedTask ? 'BoardView__column--droppable' : ''}`}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, column.id)}
                    >
                        <div className="BoardView__column-header">
                            <div className="BoardView__column-title-row">
                                {column.color && (
                                    <span
                                        className="BoardView__column-dot"
                                        style={{ backgroundColor: column.color }}
                                    />
                                )}
                                <h3 className="BoardView__column-name">{column.name}</h3>
                                <span className="BoardView__column-count">
                                    {column.tasks.filter(t => !t.is_archived && (!activeLabelFilter || t.labels?.includes(activeLabelFilter))).length}
                                </span>
                            </div>
                            <div className="BoardView__column-actions">
                                {canAdmin && (
                                    <button
                                        className="BoardView__column-edit"
                                        onClick={() => handleOpenEditColumn(column)}
                                        title="Edit column"
                                    >
                                        ✎
                                    </button>
                                )}
                                {canAdmin && (
                                    <button
                                        className="BoardView__column-delete"
                                        onClick={() => handleDeleteColumn(column.id)}
                                        title="Delete column"
                                        disabled={deletingColumnId === column.id}
                                    >
                                        {deletingColumnId === column.id
                                            ? <span className="btn-spinner btn-spinner--danger" />
                                            : '×'}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="BoardView__tasks">
                            {column.tasks
                                .filter(t => !t.is_archived && (!activeLabelFilter || t.labels?.includes(activeLabelFilter)))
                                .map(task => (
                                    <TaskCard
                                        key={task.id}
                                        task={task}
                                        boardAbbreviation={board.abbreviation}
                                        onClick={() => openTask(task)}
                                        onDragStart={(e) => handleDragStart(e, task)}
                                        onDragEnd={handleDragEnd}
                                        onContextMenu={(e) => handleTaskContextMenu(e, task)}
                                    />
                                ))}
                        </div>

                        {addingTaskColumn === column.id ? (
                            <div className="BoardView__add-task-form">
                                <input
                                    className="BoardView__add-task-input"
                                    type="text"
                                    value={newTaskTitle}
                                    onChange={(e) => setNewTaskTitle(e.target.value)}
                                    placeholder="Task title..."
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleAddTask(column.id);
                                        if (e.key === 'Escape') {
                                            setAddingTaskColumn(null);
                                            setNewTaskTitle('');
                                        }
                                    }}
                                />
                                <div className="BoardView__add-task-actions">
                                    <button
                                        className="BoardView__add-task-submit"
                                        onClick={() => handleAddTask(column.id)}
                                        disabled={addingTask}
                                    >
                                        {addingTask ? 'Adding...' : 'Add'}
                                    </button>
                                    <button
                                        className="BoardView__add-task-cancel"
                                        onClick={() => {
                                            setAddingTaskColumn(null);
                                            setNewTaskTitle('');
                                        }}
                                        disabled={addingTask}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        ) : canEdit ? (
                            <button
                                className="BoardView__add-task-btn"
                                onClick={() => setAddingTaskColumn(column.id)}
                            >
                                + Add task
                            </button>
                        ) : null}
                    </div>
                ))}

                {/* Add Column */}
                {canAdmin && <div className="BoardView__add-column">
                    {addingColumn ? (
                        <div className="BoardView__add-column-form">
                            <input
                                type="text"
                                value={newColumnName}
                                onChange={(e) => setNewColumnName(e.target.value)}
                                placeholder="Column name..."
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleAddColumn();
                                    if (e.key === 'Escape') {
                                        setAddingColumn(false);
                                        setNewColumnName('');
                                    }
                                }}
                            />
                            <div className="BoardView__add-column-actions">
                                <button onClick={handleAddColumn} disabled={addingColumnLoading}>
                                    {addingColumnLoading ? 'Adding...' : 'Add'}
                                </button>
                                <button
                                    onClick={() => { setAddingColumn(false); setNewColumnName(''); }}
                                    disabled={addingColumnLoading}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            className="BoardView__add-column-btn"
                            onClick={() => setAddingColumn(true)}
                        >
                            + Add Column
                        </button>
                    )}
                </div>}
            </div>

            {showMembers && (
                <BoardMembers
                    boardId={boardId}
                    userRole={userRole}
                    onClose={() => setShowMembers(false)}
                    onMembersChanged={fetchBoard}
                />
            )}

            {showSubmissionReview && (
                <SubmissionReview
                    boardId={boardId}
                    columns={board.columns}
                    boardAbbreviation={board.abbreviation}
                    onClose={() => setShowSubmissionReview(false)}
                    onReviewed={fetchBoard}
                />
            )}

            {selectedTask && (
                <TaskModal
                    task={selectedTask}
                    columns={board.columns}
                    boardAbbreviation={board.abbreviation}
                    existingLabels={[...new Set(board.columns.flatMap(col => col.tasks.flatMap(t => t.labels || [])))]}
                    boardMembers={[
                        ...(board.author_info ? [{ id: board.author_info.id, name: board.author_info.name, email: board.author_info.email, role: 'OWNER' }] : []),
                        ...(board.members || []).map(m => ({ id: m.user_id, name: m.name, email: m.email, role: m.role })),
                    ]}
                    onClose={closeTask}
                    onUpdate={canEdit ? handleUpdateTask : undefined}
                    onDelete={canEdit ? handleDeleteTask : undefined}
                    onMove={canEdit ? handleMoveTask : undefined}
                    onArchive={canEdit ? handleArchiveSingleTask : undefined}
                    onImmediateUpdate={handleImmediateUpdate}
                    readOnly={!canEdit}
                    boardId={boardId}
                    apiCall={apiCall}
                    currentUserUid={user?.uid}
                    canComment={!!userRole}
                />
            )}

            {/* Column Edit Modal */}
            {editingColumn && (
                <div className="ColumnEditModal__overlay" onClick={() => setEditingColumn(null)}>
                    <div className="ColumnEditModal" onClick={(e) => e.stopPropagation()}>
                        <div className="ColumnEditModal__header">
                            <h3 className="ColumnEditModal__title">Edit Column</h3>
                            <button className="ColumnEditModal__close" onClick={() => setEditingColumn(null)}>×</button>
                        </div>

                        <div className="ColumnEditModal__section">
                            <label className="ColumnEditModal__label">Column Name</label>
                            <input
                                className="ColumnEditModal__input"
                                type="text"
                                value={editColumnName}
                                onChange={(e) => setEditColumnName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveColumnEdit()}
                                autoFocus
                            />
                        </div>

                        <div className="ColumnEditModal__section">
                            <label className="ColumnEditModal__label">Dot Color</label>
                            <div className="ColumnEditModal__colors">
                                {COLUMN_COLORS.map(color => (
                                    <button
                                        key={color}
                                        className={`ColumnEditModal__color-btn ${editColumnColor === color ? 'ColumnEditModal__color-btn--active' : ''}`}
                                        style={{ backgroundColor: color }}
                                        onClick={() => setEditColumnColor(color)}
                                        title={color}
                                    />
                                ))}
                                <input
                                    type="color"
                                    className="ColumnEditModal__color-picker"
                                    value={editColumnColor}
                                    onChange={(e) => setEditColumnColor(e.target.value)}
                                    title="Custom color"
                                />
                            </div>
                        </div>

                        <div className="ColumnEditModal__section">
                            <label className="ColumnEditModal__label">Position</label>
                            <div className="ColumnEditModal__reorder">
                                <button
                                    className="ColumnEditModal__move-btn"
                                    onClick={() => handleMoveColumn(editingColumn.id, -1)}
                                    disabled={board.columns.findIndex(c => c.id === editingColumn.id) === 0 || movingColumnDir !== null}
                                >
                                    {movingColumnDir === -1 ? <><span className="btn-spinner btn-spinner--dark" /> Moving...</> : '← Move Left'}
                                </button>
                                <span className="ColumnEditModal__position">
                                    {board.columns.findIndex(c => c.id === editingColumn.id) + 1} of {board.columns.length}
                                </span>
                                <button
                                    className="ColumnEditModal__move-btn"
                                    onClick={() => handleMoveColumn(editingColumn.id, 1)}
                                    disabled={board.columns.findIndex(c => c.id === editingColumn.id) === board.columns.length - 1 || movingColumnDir !== null}
                                >
                                    {movingColumnDir === 1 ? <><span className="btn-spinner btn-spinner--dark" /> Moving...</> : 'Move Right →'}
                                </button>
                            </div>
                        </div>

                        <div className="ColumnEditModal__actions">
                            <button className="ColumnEditModal__cancel" onClick={() => setEditingColumn(null)} disabled={savingColumn}>Cancel</button>
                            <button className="ColumnEditModal__save" onClick={handleSaveColumnEdit} disabled={savingColumn}>
                                {savingColumn ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Board Edit Modal */}
            {editingBoard && (
                <div className="BoardEditModal__overlay" onClick={() => setEditingBoard(false)}>
                    <div className="BoardEditModal" onClick={(e) => e.stopPropagation()}>
                        <div className="BoardEditModal__header">
                            <h3 className="BoardEditModal__title">Edit Board</h3>
                            <button className="BoardEditModal__close" onClick={() => setEditingBoard(false)}>×</button>
                        </div>

                        <div className="BoardEditModal__body">
                        <div className="BoardEditModal__section">
                            <label className="BoardEditModal__label">Board Name</label>
                            <input
                                className="BoardEditModal__input"
                                type="text"
                                value={editBoardName}
                                onChange={(e) => setEditBoardName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveBoardEdit()}
                                maxLength={255}
                                autoFocus
                            />
                        </div>

                        <div className="BoardEditModal__section">
                            <label className="BoardEditModal__label">
                                Abbreviation <span className="BoardEditModal__hint">(up to 5 letters, used for task IDs)</span>
                            </label>
                            <input
                                className="BoardEditModal__input BoardEditModal__input--abbr"
                                type="text"
                                value={editBoardAbbr}
                                onChange={(e) => setEditBoardAbbr(e.target.value.replace(/[^a-zA-Z]/g, '').slice(0, 5))}
                                placeholder="e.g., BBTM"
                                maxLength={5}
                            />
                        </div>

                        <div className="BoardEditModal__section">
                            <label className="BoardEditModal__label">Description</label>
                            <textarea
                                className="BoardEditModal__textarea"
                                value={editBoardDesc}
                                onChange={(e) => setEditBoardDesc(e.target.value)}
                                placeholder="What's this board for?"
                                rows={3}
                            />
                        </div>

                        <div className="BoardEditModal__section">
                            <label className="BoardEditModal__label">Visibility</label>
                            <div
                                className={`BoardEditModal__toggle ${editBoardPublic ? 'BoardEditModal__toggle--active' : ''}`}
                                onClick={() => setEditBoardPublic(!editBoardPublic)}
                            >
                                <div className="BoardEditModal__toggle-track">
                                    <div className="BoardEditModal__toggle-thumb" />
                                </div>
                                <span className="BoardEditModal__toggle-label">
                                    {editBoardPublic ? 'Public — anyone with the link can view' : 'Private — only members can access'}
                                </span>
                            </div>
                            {editBoardPublic && (
                                <div className="BoardEditModal__public-link">
                                    <i className="fas fa-link" />
                                    <span>{window.location.origin}/public/board/{boardId}</span>
                                </div>
                            )}
                        </div>

                        {editBoardPublic && (() => {
                            const boardLabels = [...new Set(board.columns.flatMap(col => col.tasks.flatMap(t => t.labels || [])))];
                            const filteredPublicSuggestions = boardLabels.filter(
                                l => !editPublicLabelFilter.includes(l) && l.toLowerCase().includes(publicLabelInput.toLowerCase())
                            );
                            const handleAddPublicLabel = () => {
                                const label = publicLabelInput.trim();
                                if (label && !editPublicLabelFilter.includes(label)) {
                                    setEditPublicLabelFilter(prev => [...prev, label]);
                                    setPublicLabelInput('');
                                    setPublicLabelDropdown(false);
                                    setPublicLabelHighlight(-1);
                                }
                            };
                            const handleSelectPublicSuggestion = (suggestion) => {
                                if (!editPublicLabelFilter.includes(suggestion)) {
                                    setEditPublicLabelFilter(prev => [...prev, suggestion]);
                                }
                                setPublicLabelInput('');
                                setPublicLabelDropdown(false);
                                setPublicLabelHighlight(-1);
                            };
                            const handlePublicLabelKeyDown = (e) => {
                                if (publicLabelDropdown && filteredPublicSuggestions.length > 0) {
                                    if (e.key === 'ArrowDown') { e.preventDefault(); setPublicLabelHighlight(i => Math.min(i + 1, filteredPublicSuggestions.length - 1)); return; }
                                    if (e.key === 'ArrowUp') { e.preventDefault(); setPublicLabelHighlight(i => Math.max(i - 1, -1)); return; }
                                    if (e.key === 'Enter') {
                                        if (publicLabelHighlight >= 0) { handleSelectPublicSuggestion(filteredPublicSuggestions[publicLabelHighlight]); }
                                        else { handleAddPublicLabel(); }
                                        return;
                                    }
                                    if (e.key === 'Escape') { setPublicLabelDropdown(false); setPublicLabelHighlight(-1); return; }
                                }
                                if (e.key === 'Enter') handleAddPublicLabel();
                            };
                            return (
                                <>
                                    <div className="BoardEditModal__section BoardEditModal__section--public-settings">
                                        <label className="BoardEditModal__label">Public Board Settings</label>

                                        <label className="BoardEditModal__sublabel">Task Visibility</label>
                                        <div className="BoardEditModal__radio-group">
                                            <label className="BoardEditModal__radio-option">
                                                <input
                                                    type="radio"
                                                    name="publicTaskFilter"
                                                    value="all"
                                                    checked={editPublicTaskFilter === 'all'}
                                                    onChange={() => { setEditPublicTaskFilter('all'); setEditPublicLabelFilter([]); }}
                                                />
                                                <span>Show all tasks</span>
                                            </label>
                                            <label className="BoardEditModal__radio-option">
                                                <input
                                                    type="radio"
                                                    name="publicTaskFilter"
                                                    value="labels"
                                                    checked={editPublicTaskFilter === 'labels'}
                                                    onChange={() => setEditPublicTaskFilter('labels')}
                                                />
                                                <span>Only show tasks with specific labels</span>
                                            </label>
                                        </div>

                                        {editPublicTaskFilter === 'labels' && (
                                            <div className="BoardEditModal__label-picker">
                                                <div className="TaskModal__labels">
                                                    {editPublicLabelFilter.map((label, i) => (
                                                        <span key={i} className="TaskModal__label-tag">
                                                            {label}
                                                            <button onClick={() => setEditPublicLabelFilter(prev => prev.filter(l => l !== label))}>×</button>
                                                        </span>
                                                    ))}
                                                </div>
                                                <div className="TaskModal__label-input-row">
                                                    <div className="TaskModal__label-input-wrapper">
                                                        <input
                                                            className="TaskModal__label-input"
                                                            value={publicLabelInput}
                                                            onChange={(e) => { setPublicLabelInput(e.target.value); setPublicLabelDropdown(true); setPublicLabelHighlight(-1); }}
                                                            placeholder="Add a label..."
                                                            onKeyDown={handlePublicLabelKeyDown}
                                                            onFocus={() => setPublicLabelDropdown(true)}
                                                            onBlur={() => setTimeout(() => setPublicLabelDropdown(false), 150)}
                                                        />
                                                        {publicLabelDropdown && filteredPublicSuggestions.length > 0 && (
                                                            <div className="TaskModal__label-dropdown">
                                                                {filteredPublicSuggestions.map((s, i) => (
                                                                    <div
                                                                        key={i}
                                                                        className={`TaskModal__label-dropdown-item${i === publicLabelHighlight ? ' TaskModal__label-dropdown-item--highlighted' : ''}`}
                                                                        onMouseDown={() => handleSelectPublicSuggestion(s)}
                                                                    >
                                                                        {s}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button className="TaskModal__label-add" onMouseDown={handleAddPublicLabel}>Add</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="BoardEditModal__section">
                                        <div
                                            className={`BoardEditModal__toggle ${editPublicShowMembers ? 'BoardEditModal__toggle--active' : ''}`}
                                            onClick={() => {
                                                const next = !editPublicShowMembers;
                                                setEditPublicShowMembers(next);
                                                if (!next) setEditPublicShowContact(false);
                                            }}
                                        >
                                            <div className="BoardEditModal__toggle-track">
                                                <div className="BoardEditModal__toggle-thumb" />
                                            </div>
                                            <span className="BoardEditModal__toggle-label">Show board members on public page</span>
                                        </div>

                                        {editPublicShowMembers && (
                                            <div
                                                className={`BoardEditModal__toggle BoardEditModal__toggle--indented ${editPublicShowContact ? 'BoardEditModal__toggle--active' : ''}`}
                                                onClick={() => setEditPublicShowContact(!editPublicShowContact)}
                                            >
                                                <div className="BoardEditModal__toggle-track">
                                                    <div className="BoardEditModal__toggle-thumb" />
                                                </div>
                                                <span className="BoardEditModal__toggle-label">Show member email addresses</span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="BoardEditModal__section">
                                        <div
                                            className={`BoardEditModal__toggle ${editPublicAllowComments ? 'BoardEditModal__toggle--active' : ''}`}
                                            onClick={() => setEditPublicAllowComments(!editPublicAllowComments)}
                                        >
                                            <div className="BoardEditModal__toggle-track">
                                                <div className="BoardEditModal__toggle-thumb" />
                                            </div>
                                            <span className="BoardEditModal__toggle-label">Allow signed-in visitors to comment</span>
                                        </div>
                                    </div>

                                    {board.columns.length > 0 && (
                                        <div className="BoardEditModal__section BoardEditModal__section--column-visibility">
                                            <label className="BoardEditModal__sublabel">Column Visibility (public)</label>
                                            <p className="BoardEditModal__column-visibility-hint">
                                                Hidden columns won't appear on the public board.
                                            </p>
                                            <div className="BoardEditModal__column-list">
                                                {board.columns.map(col => {
                                                    const isHidden = editPublicHiddenColumns.includes(col.id);
                                                    return (
                                                        <div
                                                            key={col.id}
                                                            className={`BoardEditModal__column-toggle ${isHidden ? 'BoardEditModal__column-toggle--hidden' : ''}`}
                                                            onClick={() => setEditPublicHiddenColumns(prev =>
                                                                isHidden
                                                                    ? prev.filter(id => id !== col.id)
                                                                    : [...prev, col.id]
                                                            )}
                                                        >
                                                            {col.color && (
                                                                <span
                                                                    className="BoardEditModal__column-dot"
                                                                    style={{ backgroundColor: col.color }}
                                                                />
                                                            )}
                                                            <span className="BoardEditModal__column-toggle-name">{col.name}</span>
                                                            <span className="BoardEditModal__column-toggle-status">
                                                                {isHidden
                                                                    ? <><i className="fas fa-eye-slash" /> Hidden</>
                                                                    : <><i className="fas fa-eye" /> Visible</>}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    <div className="BoardEditModal__section">
                                        <div
                                            className={`BoardEditModal__toggle ${editPublicShowArchive ? 'BoardEditModal__toggle--active' : ''}`}
                                            onClick={() => setEditPublicShowArchive(!editPublicShowArchive)}
                                        >
                                            <div className="BoardEditModal__toggle-track">
                                                <div className="BoardEditModal__toggle-thumb" />
                                            </div>
                                            <span className="BoardEditModal__toggle-label">Show archive library on public page</span>
                                        </div>
                                    </div>

                                    <div className="BoardEditModal__section">
                                        <div
                                            className={`BoardEditModal__toggle ${editPublicAllowTaskSubmissions ? 'BoardEditModal__toggle--active' : ''}`}
                                            onClick={() => setEditPublicAllowTaskSubmissions(!editPublicAllowTaskSubmissions)}
                                        >
                                            <div className="BoardEditModal__toggle-track">
                                                <div className="BoardEditModal__toggle-thumb" />
                                            </div>
                                            <span className="BoardEditModal__toggle-label">Allow signed-in visitors to propose tasks</span>
                                        </div>

                                        {editPublicAllowTaskSubmissions && userRole === 'OWNER' && (
                                            <div className="BoardEditModal__review-roles">
                                                <label className="BoardEditModal__sublabel">
                                                    Who can review submissions (besides you)
                                                </label>
                                                <div className="BoardEditModal__review-roles-list">
                                                    {['ADMIN', 'EDITOR', 'VIEWER'].map(roleOption => {
                                                        const checked = editTaskReviewRoles.includes(roleOption);
                                                        return (
                                                            <label key={roleOption} className="BoardEditModal__review-role-option">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    onChange={() => setEditTaskReviewRoles(prev =>
                                                                        checked
                                                                            ? prev.filter(r => r !== roleOption)
                                                                            : [...prev, roleOption]
                                                                    )}
                                                                />
                                                                <span>{roleOption.charAt(0) + roleOption.slice(1).toLowerCase()}s</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            );
                        })()}

                        </div>{/* BoardEditModal__body */}

                        <div className="BoardEditModal__actions">
                            <button className="BoardEditModal__cancel" onClick={() => setEditingBoard(false)} disabled={savingBoard}>Cancel</button>
                            <button
                                className="BoardEditModal__save"
                                onClick={handleSaveBoardEdit}
                                disabled={!editBoardName.trim() || savingBoard}
                            >
                                {savingBoard ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showExport && (
                <ExportModal
                    board={board}
                    onClose={() => setShowExport(false)}
                />
            )}
            {showArchiveLibrary && (
                <ArchiveLibrary
                    boardId={boardId}
                    onClose={() => setShowArchiveLibrary(false)}
                    onUnarchive={fetchBoard}
                />
            )}
            {showArchiveModal && tasksForArchive.length > 0 && (
                <ArchiveModal
                    tasks={tasksForArchive}
                    boardLabels={allBoardLabels}
                    onArchive={handleArchiveTasks}
                    onClose={() => { setShowArchiveModal(false); setTasksForArchive([]); }}
                />
            )}
            {childTaskParent && (
                <div className="ChildTaskModal__overlay" onClick={() => setChildTaskParent(null)}>
                    <div className="ChildTaskModal" onClick={(e) => e.stopPropagation()}>
                        <div className="ChildTaskModal__header">
                            <h3 className="ChildTaskModal__title">Create Child Task</h3>
                            <button className="ChildTaskModal__close" onClick={() => setChildTaskParent(null)}>×</button>
                        </div>
                        <p className="ChildTaskModal__parent-hint">
                            Child of{' '}
                            <span className="ChildTaskModal__parent-id">
                                {childTaskParent.board_task_id || `#${childTaskParent.board_number || childTaskParent.id}`}
                            </span>{' '}
                            {childTaskParent.title}
                        </p>
                        <div className="ChildTaskModal__section">
                            <label className="ChildTaskModal__label">Task Title</label>
                            <input
                                className="ChildTaskModal__input"
                                type="text"
                                value={childTaskTitle}
                                onChange={(e) => setChildTaskTitle(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleCreateChildTask();
                                    if (e.key === 'Escape') setChildTaskParent(null);
                                }}
                                placeholder="Enter task title..."
                                autoFocus
                                disabled={creatingChildTask}
                            />
                        </div>
                        <div className="ChildTaskModal__actions">
                            <button className="ChildTaskModal__cancel" onClick={() => setChildTaskParent(null)} disabled={creatingChildTask}>
                                Cancel
                            </button>
                            <button
                                className="ChildTaskModal__submit"
                                onClick={handleCreateChildTask}
                                disabled={!childTaskTitle.trim() || creatingChildTask}
                            >
                                {creatingChildTask ? 'Creating...' : 'Create Child Task'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {TaskContextMenuComponent}
            {ContextMenuComponent}
        </div>
    );
};

export default BoardView;
